<template>
  <div class="page-wrapper">
    <div class="search-page">

      <PageHeader title="Search" />

      <!-- Search Form -->
      <form @submit.prevent="performSearch" class="search-form">
        <div class="form-grid">
          <div class="form-field">
            <label for="title">Title</label>
            <input
              id="title"
              v-model="searchParams.title"
              type="text"
              class="search-input"
              placeholder="Title"
            />
          </div>

          <div class="form-field">
            <label for="composer">Composer</label>
            <input
              id="composer"
              v-model="searchParams.composer"
              type="text"
              class="search-input"
              placeholder="Composer"
            />
          </div>

          <div class="form-field">
            <label for="organist">Organist</label>
            <input
              id="organist"
              v-model="searchParams.organist"
              type="text"
              class="search-input"
              placeholder="Organist"
            />
          </div>

          <div class="form-field">
            <label for="album">Album</label>
            <input
              id="album"
              v-model="searchParams.album"
              type="text"
              class="search-input"
              placeholder="Album"
            />
          </div>
        </div>

        <div class="form-actions">
          <div class="display-toggle">
            <label>Display as:</label>
            <label class="radio-label">
              <input
                type="radio"
                v-model="searchParams.display"
                value="tracks"
              />
              Tracks
            </label>
            <label class="radio-label">
              <input
                type="radio"
                v-model="searchParams.display"
                value="albums"
              />
              Albums
            </label>
          </div>

          <button type="submit" class="btn btn-primary btn-large" :disabled="!hasSearchParams">
            Search
          </button>
        </div>
      </form>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading">
        Searching...
      </div>

      <!-- Results -->
      <div v-else-if="hasSearched" class="results-section">

        <!-- Album Results -->
        <template v-if="searchParams.display === 'albums'">
          <div v-if="albums.length > 0">
            <h3 class="results-header">{{ albums.length }} album{{ albums.length !== 1 ? 's' : '' }} found</h3>
            <AlbumList
              :albums="albums"
              :is-loading="false"
              :titlefilter="searchParams.title"
              :organistfilter="searchParams.organist"
              :composerfilter="searchParams.composer"
            />
          </div>
          <div v-else class="no-results">
            No albums found matching your search criteria.
          </div>
        </template>

        <!-- Track Results -->
        <template v-else>
          <div v-if="tracks.length > 0">
            <h3 class="results-header">{{ tracks.length }} track{{ tracks.length !== 1 ? 's' : '' }} found</h3>

            <div class="track-list">
              <!-- Column Headers -->
              <div class="track-header-row">
                <div class="track-number-header">#</div>
                <div class="track-artist-header">Artist</div>
                <div class="track-title-header">Title</div>
                <div class="track-composer-header">Composer</div>
                <div class="track-album-header">Album</div>
                <div class="track-sheetmusic-header">Sheet Music</div>
                <div class="track-organ-header">Organ</div>
                <div class="track-request-header">Request</div>
              </div>

              <!-- Track Rows -->
              <div
                v-for="(track, index) in tracks"
                :key="track.trackid"
                class="track-row"
              >
                <div class="track-number">{{ index + 1 }}</div>

                <!-- Artist Column -->
                <div class="track-artist">
                  <router-link
                    v-if="track.artist"
                    :to="`/artist/${track.artistid}`"
                    class="track-meta-link"
                  >
                    {{ track.artist }}
                  </router-link>
                </div>

                <!-- Title Column -->
                <div class="track-title-col">
                  <div class="track-title">{{ track.title || track.tracktitle }}</div>

                  <!-- Star Rating Display (under title) -->
                  <StarRating
                    :model-value="track.rating || 0"
                    :read-only="true"
                    :compact="true"
                    class="search-track-rating"
                  />

                  <div v-if="track.duration" class="track-duration">{{ track.duration }}</div>
                </div>

                <!-- Composer Column -->
                <div class="track-composer">
                  <router-link
                    v-if="track.composer"
                    :to="`/composer/${track.composerid}`"
                    class="track-meta-link"
                  >
                    {{ track.composer }}
                  </router-link>
                </div>

                <!-- Album Column -->
                <div class="track-album-col">
                  <router-link
                    v-if="track.albumid"
                    :to="`/album/${track.albumid}`"
                    class="track-meta-link"
                  >
                    {{ track.album || 'View Album' }}
                  </router-link>
                </div>

                <!-- Sheet Music Column -->
                <div class="track-sheetmusic">
                  <PdfLink v-if="track.PDFsheetmusic" :url="track.PDFsheetmusic" />
                  <BuySheetMusicLink v-if="track.buy" :url="track.buy" />
                </div>

                <!-- Organ Column -->
                <div class="track-organ-col">
                  <div v-if="trackOrgans[track.organid]" class="track-organ-info">
                    <div class="track-organ-location">
                      {{ formatOrganLocation(trackOrgans[track.organid]) }}
                    </div>
                    <div class="track-organ-details">
                      <a
                        v-if="trackOrgans[track.organid].link"
                        :href="trackOrgans[track.organid].link"
                        class="organ-link"
                        target="_blank"
                        rel="noopener"
                      >
                        {{ formatOrganSpecs(trackOrgans[track.organid]) }}
                      </a>
                      <span v-else>{{ formatOrganSpecs(trackOrgans[track.organid]) }}</span>
                      <span v-if="trackOrgans[track.organid].glat && trackOrgans[track.organid].glong">
                        -
                      <a
                          :href="`https://maps.google.com/?q=${trackOrgans[track.organid].glat},${trackOrgans[track.organid].glong}`"
                          class="organ-map-link"
                          target="_blank"
                          rel="noopener"
                        >
                          Map
                        </a>
                      </span>
                    </div>
                  </div>
                  <div v-else-if="track.organid && track.organid !== '9999'" class="track-organ-loading">
                    Loading...
                  </div>
                </div>

                <!-- Request Column -->
                <div class="track-request">
                  <RequestButton v-if="track.songid" :trackId="track.songid" />
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-results">
            No tracks found matching your search criteria.
          </div>
        </template>
      </div>

    </div>
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue'
import AlbumList from '../components/AlbumList.vue'
import PdfLink from '../components/PdfLink.vue'
import BuySheetMusicLink from '../components/BuySheetMusicLink.vue'
import RequestButton from '../components/RequestButton.vue'
import StarRating from '../components/StarRating.vue'
import { normalizeAlbumList } from '../utils/normalizeAlbum.js'
import { normalizeTrackList } from '../utils/normalizeTrack.js'
import { onRatingUpdate } from '../utils/ratingEvents.js'

export default {
  name: 'SearchPage',

  components: {
    PageHeader,
    AlbumList,
    PdfLink,
    BuySheetMusicLink,
    RequestButton,
    StarRating
  },

  data () {
    return {
      searchParams: {
        title: '',
        composer: '',
        organist: '',
        album: '',
        display: 'tracks'
      },
      albums: [],
      tracks: [],
      trackOrgans: {},
      isLoading: false,
      hasSearched: false,
      ratingUnsubscribe: null
    }
  },

  computed: {
    hasSearchParams () {
      return this.searchParams.title ||
             this.searchParams.composer ||
             this.searchParams.organist ||
             this.searchParams.album
    }
  },

  mounted () {
    console.log('🔍 SearchPage mounted - setting up rating listener')

    // Listen for global rating updates
    this.ratingUnsubscribe = onRatingUpdate((data) => {
      this.handleRatingUpdate(data)
    })

    // Load from URL query parameters if present
    this.loadFromUrl()
    document.title = 'Organlive.com - Library Search'
  },

  beforeUnmount () {
    // Clean up rating event listener
    if (this.ratingUnsubscribe) {
      this.ratingUnsubscribe()
      console.log('🔍 SearchPage: Rating listener cleaned up')
    }
  },

  methods: {
    /**
     * Handle global rating update event
     * Updates track rating if it's in the search results
     */
    handleRatingUpdate (data) {
      const { trackId, averageRating } = data

      console.log('🔔 SearchPage received rating update:', {
        trackId,
        averageRating,
        tracksCount: this.tracks.length
      })

      // Find and update the track in search results
      const track = this.tracks.find(t =>
        String(t.songid) === String(trackId) ||
        String(t.trackid) === String(trackId)
      )

      if (track) {
        // Only update if we have a valid average (don't set to 0 which would hide stars)
        if (averageRating > 0) {
          track.rating = averageRating
          console.log('✓ Updated track rating in SearchPage:', trackId, averageRating)

          // Force re-render by creating new array reference
          this.tracks = [...this.tracks]
        } else {
          console.warn('⚠️ SearchPage: Ignoring invalid average rating:', averageRating)
        }
      }
    },

    loadFromUrl () {
      const query = this.$route.query

      if (query.title || query.composer || query.organist || query.album) {
        this.searchParams.title = query.title || ''
        this.searchParams.composer = query.composer || ''
        this.searchParams.organist = query.organist || ''
        this.searchParams.album = query.album || ''
        this.searchParams.display = query.display || 'tracks'

        // Perform search with URL parameters
        this.executeSearch()
      }
    },

    async performSearch () {
      if (!this.hasSearchParams) {
        return
      }

      // Update URL with search parameters
      const query = {}
      if (this.searchParams.title) query.title = this.searchParams.title
      if (this.searchParams.composer) query.composer = this.searchParams.composer
      if (this.searchParams.organist) query.organist = this.searchParams.organist
      if (this.searchParams.album) query.album = this.searchParams.album
      query.display = this.searchParams.display

      // Update URL without reloading page
      this.$router.push({ path: '/search', query })

      // Perform the search
      await this.executeSearch()
    },

    async executeSearch () {
      this.isLoading = true
      this.hasSearched = false
      this.albums = []
      this.tracks = []
      this.trackOrgans = {}

      // Build API URL
      const params = new URLSearchParams()
      if (this.searchParams.title) params.append('title', this.searchParams.title)
      if (this.searchParams.composer) params.append('composer', this.searchParams.composer)
      if (this.searchParams.organist) params.append('organist', this.searchParams.organist)
      if (this.searchParams.album) params.append('album', this.searchParams.album)
      params.append('display', this.searchParams.display)

      const url = `https://api.organlive.com/search?${params.toString()}`

      console.log('🔍 SearchPage: Fetching from', url)

      try {
        const response = await fetch(url)
        const json = await response.json()

        if (json && (Array.isArray(json) ? json.length > 0 : Object.keys(json).length > 0)) {
          const firstItem = Array.isArray(json) ? json[0] : json[Object.keys(json)[0]]
          console.log('📊 First search result:', firstItem)
        }

        if (this.searchParams.display === 'albums') {
          // Handle album results
          this.albums = normalizeAlbumList(json)
          console.log('📊 Albums loaded:', this.albums.length)
        } else {
          // Handle track results
          let tracks = normalizeTrackList(json)

          // CRITICAL: Convert rating strings to numbers (API returns strings)
          tracks = tracks.map(track => ({
            ...track,
            rating: track.rating ? parseFloat(track.rating) : 0
          }))

          this.tracks = tracks

          console.log('📊 Tracks loaded:', this.tracks.length)
          console.log('📊 Tracks with ratings:', this.tracks.filter(t => t.rating > 0).length)

          // Load organ data for tracks
          await this.loadTrackOrgans()
        }

        this.hasSearched = true
      } catch (err) {
        console.error('❌ Search error:', err)
        this.hasSearched = true
      } finally {
        this.isLoading = false
      }
    },

    async loadTrackOrgans () {
      console.log('=== loadTrackOrgans called ===')
      console.log('Total tracks:', this.tracks.length)

      // Get unique organ IDs from tracks (excluding 9999 and invalid IDs)
      const organIds = [...new Set(
        this.tracks
          .map(t => t.organid)
          .filter(id => id && id !== '9999' && id !== 9999 && id !== '' && id !== null)
      )]

      console.log('Unique organ IDs to load:', organIds)

      if (organIds.length === 0) {
        console.log('No organ IDs to load - exiting')
        return
      }

      // Fetch organ data for each unique organID
      const organPromises = organIds.map(async (organId) => {
        try {
          const url = `https://api.organlive.com/library/organ/${organId}`
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          const json = await response.json()
          return { organId: String(organId), data: json }
        } catch (err) {
          console.error(`Organ load error for ${organId}:`, err)
          return { organId: String(organId), data: null }
        }
      })

      const results = await Promise.all(organPromises)

      // Build new trackOrgans object
      const newTrackOrgans = {}
      results.forEach(({ organId, data }) => {
        if (data) {
          newTrackOrgans[String(organId)] = data
        }
      })

      // Reassign entire object to trigger Vue reactivity
      this.trackOrgans = newTrackOrgans

      console.log('✓ Organs loaded:', Object.keys(this.trackOrgans).length)
    },

    formatOrganLocation (organ) {
      if (!organ) return ''
      const parts = []
      if (organ.institution) parts.push(organ.institution)
      if (organ.city) parts.push(organ.city)
      if (organ.state) parts.push(organ.state)
      if (organ.country) parts.push(organ.country)
      return parts.join(', ')
    },

    formatOrganSpecs (organ) {
      if (!organ) return ''
      const parts = []
      if (organ.datebuilt) parts.push(organ.datebuilt)
      if (organ.manuals || organ.ranks) {
        parts.push(`${organ.manuals || '?'}/${organ.ranks || '?'}`)
      }
      if (organ.builder) parts.push(organ.builder)
      if (organ.rebuilder) parts.push(organ.rebuilder)
      return parts.join(' ')
    }
  }
}
</script>

<style scoped>
/* Page-specific styles only - wrapper now global */

.search-page {
  background: #222;
  color: var(--color-white);
  padding: var(--spacing-element);
  border-radius: var(--radius-large);
  max-width: 1400px;
  margin: 0 auto;
}

/* Search Form */
.search-form {
  background: #2a2a2a;
  padding: var(--spacing-element);
  border-radius: var(--radius-default);
  margin-bottom: 2rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field label {
  color: var(--color-medium-gray);
  font-size: 0.9rem;
  font-weight: 500;
}

.search-input {
  background: #333;
  color: var(--color-white);
  border: 1px solid #555;
  border-radius: var(--radius-small);
  padding: 0.7rem;
  font-size: 1rem;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary-hover);
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.display-toggle {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.display-toggle > label {
  color: var(--color-medium-gray);
  font-weight: 500;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--color-white);
  cursor: pointer;
}

.radio-label input[type="radio"] {
  cursor: pointer;
}

/* Loading and Results */
.loading {
  text-align: center;
  padding: 2rem;
  font-size: 1.1rem;
  color: var(--color-medium-gray);
}

.results-section {
  margin-top: 1rem;
}

.results-header {
  color: var(--color-white);
  font-size: 1.2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #444;
}

.no-results {
  text-align: center;
  padding: 2rem;
  color: var(--color-medium-gray);
  font-size: 1rem;
}

/* Track List Styles */
.track-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.track-header-row {
  display: grid;
  grid-template-columns: 3rem minmax(120px, 0.7fr) minmax(150px, 1fr) minmax(120px, 0.6fr) minmax(120px, 0.6fr) 120px minmax(180px, 1fr) 100px;
  gap: 1rem;
  padding: 0.8rem 0;
  border-bottom: 2px solid #555;
  font-weight: 600;
  color: var(--color-medium-gray);
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.track-number-header,
.track-artist-header,
.track-title-header,
.track-composer-header,
.track-album-header,
.track-sheetmusic-header,
.track-organ-header,
.track-request-header {
  display: flex;
  align-items: center;
}

.track-number-header {
  text-align: right;
  justify-content: flex-end;
}

.track-row {
  display: grid;
  grid-template-columns: 3rem minmax(120px, 0.7fr) minmax(150px, 1fr) minmax(120px, 0.6fr) minmax(120px, 0.6fr) 120px minmax(180px, 1fr) 100px;
  gap: 1rem;
  align-items: start;
  padding: 0.8rem 0;
  border-bottom: 1px solid #333;
  transition: background var(--transition-fast);
}

.track-row:hover {
  background: #2a2a2a;
}

.track-number {
  font-weight: bold;
  color: #bbb;
  font-size: 1rem;
  text-align: right;
  padding-top: 0.2rem;
}

.track-artist,
.track-composer,
.track-album-col {
  color: var(--color-primary-hover);
}

.track-title-col {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.track-title {
  color: var(--color-white);
  font-weight: 500;
  font-size: 1rem;
}

/* Rating display in search results */
.search-track-rating {
  display: inline-block;
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
}

.track-duration {
  color: var(--color-dark-gray);
  font-size: var(--font-size-tiny);
}

.track-sheetmusic {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-items: flex-start;
}

.track-organ-col {
  font-size: var(--font-size-tiny);
}

.track-organ-info {
  line-height: 1.4;
}

.track-organ-location {
  color: #ddd;
  margin-bottom: 0.3rem;
}

.track-organ-details {
  color: var(--color-medium-gray);
}

.track-organ-loading {
  color: var(--color-dark-gray);
  font-style: italic;
  font-size: var(--font-size-tiny);
}

.track-request {
  display: flex;
  align-items: flex-start;
}

.track-meta-link {
  color: var(--color-primary-hover);
  text-decoration: none;
}

.track-meta-link:hover {
  text-decoration: underline;
}

.organ-link,
.organ-map-link {
  color: var(--color-primary-hover);
  text-decoration: none;
}

.organ-link:hover,
.organ-map-link:hover {
  text-decoration: underline;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .display-toggle {
    justify-content: center;
  }

  .track-header-row {
    display: none;
  }

  .track-row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem 0;
  }

  .track-number {
    text-align: left;
    font-size: 0.9rem;
    padding: 0;
  }

  .track-number::before {
    content: 'Track ';
    color: var(--color-dark-gray);
  }

  .track-sheetmusic {
    flex-direction: row;
    gap: 0.5rem;
  }

  .track-organ-col {
    padding: 0.5rem;
    background: #2a2a2a;
    border-radius: var(--radius-small);
  }
}
</style>
